import * as React from "react";

import { MARKET_DATA_UNAVAILABLE_COPY } from "@/constants/marketDataUnavailableCopy";
import { MARKET_DATA_RESOLVE_DIALOG_COPY } from "@/constants/marketDataResolveDialogCopy";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { MarketDataUnavailablePayload } from "@/types/marketDataUnavailable";
import {
  formatExecutionCandleTypeLabel,
  isExecutionCandleTypeDisabledForBarBudget,
  normalizeExecutionCandleType,
} from "@/utils/executionCandleTimeframe";
import {
  buildInlineTradeReviewCoarserTimeframeHint,
  formatMarketDataEarliestAvailable,
  isMarketDataHistoryInsufficientReason,
} from "@/utils/marketDataUnavailable";

export type MarketDataUnavailableChartOverlayProps = {
  payload: MarketDataUnavailablePayload;
  onChangeDataSource: () => void;
  currentCandleType?: string;
  barBudgetMinCandleType?: string | null;
  onPickCandleType?: (timeframe: string) => void;
  className?: string;
};

function OverlayMessage({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground",
        className
      )}
    >
      {children}
    </p>
  );
}

/** Centered chart empty state — matches inline Trade Review row styling. */
export function MarketDataUnavailableChartOverlay({
  payload,
  onChangeDataSource,
  currentCandleType,
  barBudgetMinCandleType,
  onPickCandleType,
  className,
}: MarketDataUnavailableChartOverlayProps): React.ReactElement {
  const isConsentRequired = payload.reason === "market_data_consent_required";
  const isHistoryInsufficient = isMarketDataHistoryInsufficientReason(payload.reason);
  const earliest = formatMarketDataEarliestAvailable(payload.earliestAvailable);
  const tradeReviewHint = buildInlineTradeReviewCoarserTimeframeHint(
    payload.suggestedCandleTypes
  );

  const currentNormalized = normalizeExecutionCandleType(currentCandleType);
  const coarserOptions = React.useMemo(() => {
    if (!onPickCandleType) return [];
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string; disabled: boolean }> = [];
    for (const raw of payload.suggestedCandleTypes ?? []) {
      const normalized = normalizeExecutionCandleType(raw);
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      if (normalized === currentNormalized) continue;
      options.push({
        value: normalized,
        label: formatExecutionCandleTypeLabel(normalized),
        disabled: isExecutionCandleTypeDisabledForBarBudget(
          normalized,
          barBudgetMinCandleType
        ),
      });
    }
    return options;
  }, [
    barBudgetMinCandleType,
    currentCandleType,
    currentNormalized,
    onPickCandleType,
    payload.suggestedCandleTypes,
  ]);

  const ctaLabel = isConsentRequired
    ? MARKET_DATA_RESOLVE_DIALOG_COPY.confirmMarketDataSource
    : isHistoryInsufficient
      ? MARKET_DATA_UNAVAILABLE_COPY.liquid_history_insufficient.ctaChangeSource
      : MARKET_DATA_RESOLVE_DIALOG_COPY.confirmMarketDataSource;

  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 p-6",
        className
      )}
      data-market-data-unavailable=""
      data-market-data-unavailable-reason={payload.reason}
    >
      <div className="absolute inset-0 bg-background/90" aria-hidden />
      <div className="relative z-[1] flex max-w-md flex-col items-center gap-4">
        <div className="flex flex-col gap-2 text-center">
          <OverlayMessage>{payload.message}</OverlayMessage>
          {payload.tradeAnalyticsAvailable ? (
            <OverlayMessage className="text-xs opacity-90">
              Trade stats and lists still work — only OHLC charts need a fix.
            </OverlayMessage>
          ) : null}
          {!isConsentRequired && !onPickCandleType ? (
            <OverlayMessage className="text-xs opacity-90">{tradeReviewHint}</OverlayMessage>
          ) : null}
          {earliest && isHistoryInsufficient ? (
            <OverlayMessage className="text-xs opacity-75">
              Earliest native data available from {earliest}.
            </OverlayMessage>
          ) : null}
        </div>
        <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-2">
          {coarserOptions.map((option) => (
            <Button
              key={option.value}
              type="button"
              variant="outline-brand"
              size="sm"
              data-font="primary"
              disabled={option.disabled}
              className="h-8 px-4 text-[0.75rem] normal-case tracking-normal"
              onClick={() => onPickCandleType?.(option.value)}
            >
              Switch to {option.label}
            </Button>
          ))}
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            onClick={onChangeDataSource}
          >
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
